import {
  getAutoReleaseDate,
  getContentDiffRows,
  getReconciliationInfo,
  PaymentRecoveryCheckoutDetails,
} from "@dashboard/checkouts/utils";
import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DashboardCard } from "@dashboard/components/Card";
import { Date as DateLabel, DateTime } from "@dashboard/components/Date";
import { DetailPageLayout } from "@dashboard/components/Layouts";
import { Pill } from "@dashboard/components/Pill";
import { CheckoutAuthorizeStatusEnum } from "@dashboard/graphql";
import { OrderChannelSectionCard } from "@dashboard/orders/components/OrderChannelSectionCard";
import OrderTransaction from "@dashboard/orders/components/OrderTransaction";
import { Box, Skeleton, Text } from "@saleor/macaw-ui-next";
import React from "react";
import { useIntl } from "react-intl";

import { paymentRecoveryMessages } from "../../messages";
import { checkoutListPath } from "../../urls";
import PaymentRecoveryCustomerCard from "./PaymentRecoveryCustomerCard";
import PaymentRecoveryLineItems from "./PaymentRecoveryLineItems";
import PaymentRecoveryOrderSummary from "./PaymentRecoveryOrderSummary";
import PaymentRecoveryPaymentSummary from "./PaymentRecoveryPaymentSummary";

interface PaymentRecoveryDetailsPageProps {
  checkout: PaymentRecoveryCheckoutDetails | undefined;
  loading: boolean;
  onCreateOrder: () => void;
  onRefund: () => void;
}

const SummaryRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Box display="flex" justifyContent="space-between" alignItems="center" gap={3}>
    <Text size={2} color="default2">
      {label}
    </Text>
    <Box __textAlign="right">{children}</Box>
  </Box>
);

// Per-transaction action buttons (CHARGE/CANCEL) are intentionally not
// wired here (see the OrderTransaction usage below, showActions={false}):
// none of this page's actions are permission-gated yet (see the top-nav
// comment below), and a bare per-transaction action button would bypass
// whatever that gating ends up being. This is a required prop of
// OrderTransaction with nothing to call.
const noopTransactionAction = () => undefined;

export const PaymentRecoveryDetailsPage = ({
  checkout,
  loading,
  // Unused while Refund/Void and Create order are hidden platform-wide
  // (see the top-nav comment below) -- kept in the props interface so the
  // parent (which owns the modals these open) doesn't need to change when
  // these are re-enabled.
  onCreateOrder: _onCreateOrder,
  onRefund: _onRefund,
}: PaymentRecoveryDetailsPageProps) => {
  const intl = useIntl();
  const autoReleaseDate = checkout ? getAutoReleaseDate(checkout) : null;
  const isReleaseDue = !!autoReleaseDate && autoReleaseDate.getTime() <= Date.now();
  // An order created from a partially covered checkout lands underpaid, so the
  // only safe remedy there is giving the money back.
  const isFullyCovered = checkout?.authorizeStatus === CheckoutAuthorizeStatusEnum.FULL;
  const kountDecision = checkout?.metadata.find(item => item.key === "kount_decision")?.value;
  const omniscore = checkout?.metadata.find(item => item.key === "omniscore")?.value;
  const reconciliation = getReconciliationInfo(checkout?.metadata);
  const contentDiffRows = getContentDiffRows(reconciliation?.diff ?? null);

  return (
    <DetailPageLayout>
      <TopNav
        href={checkoutListPath}
        title={checkout?.email ?? intl.formatMessage(paymentRecoveryMessages.emptyTitle)}
        subtitle={
          checkout ? (
            <Text size={2} color="default2">
              {checkout.channel.name} · <DateTime date={checkout.created} plain />
            </Text>
          ) : undefined
        }
      >
        {/* Refund/Void and Create order are hidden platform-wide on this page
            for now, regardless of reconciliation status: neither action has
            its permission checks wired up yet, so a click here can't
            actually be gated the way it needs to be before it's safe to
            expose. This whole page is view-only until that's implemented --
            not just the needs_review case. Re-enable per-action once
            permissions land, not by removing this block wholesale. */}
      </TopNav>

      <DetailPageLayout.Content>
        {loading && !checkout ? (
          <Skeleton />
        ) : (
          <>
            {!isFullyCovered && (
              <Box paddingX={6} paddingBottom={4}>
                <Text color="critical1">
                  {intl.formatMessage(paymentRecoveryMessages.partialAuthorizeHint)}
                </Text>
              </Box>
            )}

            <PaymentRecoveryLineItems lines={checkout?.lines ?? []} />

            {checkout && (
              <Box display="grid" __gridTemplateColumns="repeat(2, 1fr)" gap={4} marginTop={4}>
                <PaymentRecoveryOrderSummary checkout={checkout} />
                <PaymentRecoveryPaymentSummary checkout={checkout} />
              </Box>
            )}

            <Box marginTop={4}>
              <Text fontWeight="bold" display="block" marginBottom={3}>
                {intl.formatMessage(paymentRecoveryMessages.paymentsSection)}
              </Text>
              <Box display="flex" flexDirection="column" gap={4}>
                {checkout?.transactions?.length ? (
                  checkout.transactions.map(transaction => (
                    <OrderTransaction
                      key={transaction.id}
                      transaction={transaction}
                      onTransactionAction={noopTransactionAction}
                      showActions={false}
                    />
                  ))
                ) : (
                  <Text color="default2">
                    {intl.formatMessage(paymentRecoveryMessages.noTransactions)}
                  </Text>
                )}
              </Box>
            </Box>
          </>
        )}
      </DetailPageLayout.Content>

      <DetailPageLayout.RightSidebar>
        {reconciliation && (
          <DashboardCard>
            <DashboardCard.Header>
              <DashboardCard.Title>
                {intl.formatMessage(paymentRecoveryMessages.reconciliationSection)}
              </DashboardCard.Title>
            </DashboardCard.Header>
            <DashboardCard.Content>
              <Box display="flex" flexDirection="column" gap={3}>
                <SummaryRow label={intl.formatMessage(paymentRecoveryMessages.reconciliationStatus)}>
                  <Pill
                    color={
                      reconciliation.status === "needs_review"
                        ? "error"
                        : reconciliation.status === "resolved"
                          ? "info"
                          : "warning"
                    }
                    label={reconciliation.status.replace(/_/g, " ")}
                  />
                </SummaryRow>
                {reconciliation.gateway && (
                  <SummaryRow label={intl.formatMessage(paymentRecoveryMessages.colGateway)}>
                    {reconciliation.gateway}
                  </SummaryRow>
                )}
                {reconciliation.note && (
                  <SummaryRow label={intl.formatMessage(paymentRecoveryMessages.reconciliationReason)}>
                    <Text size={2} __wordBreak="break-word">
                      {reconciliation.note}
                    </Text>
                  </SummaryRow>
                )}
                {reconciliation.updatedAt && (
                  <SummaryRow label={intl.formatMessage(paymentRecoveryMessages.colLastEvent)}>
                    <DateTime date={reconciliation.updatedAt} plain />
                  </SummaryRow>
                )}
                {contentDiffRows.length > 0 && (
                  <Box display="flex" flexDirection="column" gap={2} marginTop={2}>
                    <Text size={2} fontWeight="bold">
                      {intl.formatMessage(paymentRecoveryMessages.contentDiffTitle)}
                    </Text>
                    {contentDiffRows.map(row => (
                      <Box key={row.field} display="flex" flexDirection="column" gap={1}>
                        <Text size={2} color="default2">
                          {row.field}
                        </Text>
                        <Text size={2} __wordBreak="break-word">
                          {row.before} → {row.after}
                        </Text>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </DashboardCard.Content>
          </DashboardCard>
        )}

        {(kountDecision || omniscore) && (
          <DashboardCard marginTop={reconciliation ? 4 : undefined}>
            <DashboardCard.Header>
              <DashboardCard.Title>
                {intl.formatMessage(paymentRecoveryMessages.fraudSection)}
              </DashboardCard.Title>
            </DashboardCard.Header>
            <DashboardCard.Content>
              <Box display="flex" flexDirection="column" gap={3}>
                {kountDecision && <SummaryRow label="Kount decision">{kountDecision}</SummaryRow>}
                {omniscore && <SummaryRow label="Omniscore">{omniscore}</SummaryRow>}
              </Box>
            </DashboardCard.Content>
          </DashboardCard>
        )}

        <DashboardCard marginTop={reconciliation || kountDecision || omniscore ? 4 : undefined}>
          <DashboardCard.Content>
            <Box display="flex" flexDirection="column" gap={3}>
              <SummaryRow label={intl.formatMessage(paymentRecoveryMessages.colActivity)}>
                {checkout && <DateLabel date={checkout.lastChange} />}
              </SummaryRow>
              <SummaryRow label={intl.formatMessage(paymentRecoveryMessages.autoRefundLabel)}>
                {/* checkout is undefined while still loading -- autoReleaseDate
                    is then also null, which would otherwise read identically to
                    "this channel has auto-refund disabled" before the real
                    answer is even known. Render nothing until checkout arrives,
                    same as colActivity above. */}
                {!checkout ? null : !autoReleaseDate ? (
                  <Text size={2} color="default2">
                    {intl.formatMessage(paymentRecoveryMessages.releaseDisabled)}
                  </Text>
                ) : isReleaseDue ? (
                  // A bare relative date reads as "3 months ago", which
                  // looks like history rather than an overdue action.
                  <Text size={2} color="critical1">
                    {intl.formatMessage(paymentRecoveryMessages.releaseCountdownPassed)}
                  </Text>
                ) : (
                  <Text size={2}>
                    <DateLabel date={autoReleaseDate.toISOString()} />
                  </Text>
                )}
              </SummaryRow>
            </Box>
          </DashboardCard.Content>
        </DashboardCard>

        {checkout && (
          <Box marginTop={4}>
            <PaymentRecoveryCustomerCard checkout={checkout} />
          </Box>
        )}

        <Box marginTop={4}>
          <OrderChannelSectionCard channel={checkout?.channel} />
        </Box>
      </DetailPageLayout.RightSidebar>
    </DetailPageLayout>
  );
};

PaymentRecoveryDetailsPage.displayName = "PaymentRecoveryDetailsPage";
export default PaymentRecoveryDetailsPage;
